import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Plus, Pencil, Trash2, Loader2, FileSpreadsheet } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { 
  getAllHabitPresets,
  createHabitPreset,
  updateHabitPreset,
  deleteHabitPreset,
  toggleHabitPresetStatus 
} from '@/lib/firebase/queries/habitPresets';
import { FrequencySelector } from '@/components/customer/FrequencySelector';
import type { HabitPreset, HabitCategory, HabitFrequencyConfig } from '@/types/habits';
import { habitBadges, HABIT_CATEGORY_ORDER } from '@/lib/constants/habitBadges';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { toast } from 'sonner';
import { HabitPresetExcelUpload, type ParsedPreset } from './HabitPresetExcelUpload';

export function HabitPresetManagement() {
  const [presets, setPresets] = useState<HabitPreset[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingPreset, setEditingPreset] = useState<HabitPreset | null>(null);
  const [deletePreset, setDeletePreset] = useState<HabitPreset | null>(null);
  const [showExcelUpload, setShowExcelUpload] = useState(false);
  const { currentUser } = useAuth();
  const { t, language, dir } = useLanguage();
  const isRTL = language === 'ar';

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    nameAr: '',
    category: 'Exercises' as HabitCategory,
    description: '',
    descriptionAr: '',
  });
  const [frequencyConfig, setFrequencyConfig] = useState<HabitFrequencyConfig>({
    frequency: 'daily',
    timesPerFrequency: 1,
    duration: 90,
  });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchPresets();
  }, []);

  const fetchPresets = async () => {
    try {
      setLoading(true);
      const fetchedPresets = await getAllHabitPresets();
      setPresets(fetchedPresets);
    } catch (error) {
      console.error('Error fetching presets:', error);
      toast.error('Failed to load presets');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (preset?: HabitPreset) => {
    if (preset) {
      setEditingPreset(preset);
      setFormData({
        name: preset.name,
        nameAr: preset.nameAr || '',
        category: preset.category,
        description: preset.description || '',
        descriptionAr: preset.descriptionAr || '',
      });
      setFrequencyConfig(preset.frequencyConfig || {
        frequency: 'daily',
        timesPerFrequency: 1,
        duration: 90,
      });
    } else {
      setEditingPreset(null);
      setFormData({
        name: '',
        nameAr: '',
        category: 'Exercises',
        description: '',
        descriptionAr: '',
      });
      setFrequencyConfig({
        frequency: 'daily',
        timesPerFrequency: 1,
        duration: 90,
      });
    }
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter a habit name');
      return;
    }

    if (!currentUser?.uid) {
      toast.error('You must be logged in');
      return;
    }

    try {
      setIsSaving(true);

      if (editingPreset) {
        await updateHabitPreset(editingPreset.id, {
          name: formData.name.trim(),
          nameAr: formData.nameAr.trim(),
          category: formData.category,
          description: formData.description.trim(),
          descriptionAr: formData.descriptionAr.trim(),
          frequencyConfig,
        });
        toast.success('Preset updated successfully');
      } else {
        await createHabitPreset({
          name: formData.name.trim(),
          category: formData.category,
          createdBy: currentUser.uid,
          nameAr: formData.nameAr.trim() || undefined,
          description: formData.description.trim() || undefined,
          descriptionAr: formData.descriptionAr.trim() || undefined,
          frequencyConfig: frequencyConfig,
        });
        toast.success('Preset created successfully');
      }

      await fetchPresets();
      setShowDialog(false);
    } catch (error) {
      console.error('Error saving preset:', error);
      toast.error('Failed to save preset');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletePreset) return;

    try {
      await deleteHabitPreset(deletePreset.id);
      toast.success('Preset deleted successfully');
      await fetchPresets();
      setDeletePreset(null);
    } catch (error) {
      console.error('Error deleting preset:', error);
      toast.error('Failed to delete preset');
    }
  };

  const handleToggleStatus = async (preset: HabitPreset) => {
    try {
      await toggleHabitPresetStatus(preset.id, !preset.isActive);
      toast.success(`Preset ${!preset.isActive ? 'activated' : 'deactivated'}`);
      await fetchPresets();
    } catch (error) {
      console.error('Error toggling preset status:', error);
      toast.error('Failed to update preset status');
    }
  };

  const handleExcelImport = async (importedPresets: ParsedPreset[]) => {
    if (!currentUser?.uid) {
      toast.error('You must be logged in to import presets');
      return;
    }

    try {
      // Create all imported presets
      await Promise.all(
        importedPresets.map(preset =>
          createHabitPreset({
            name: preset.name,
            nameAr: preset.nameAr,
            category: preset.category,
            description: preset.description,
            descriptionAr: preset.descriptionAr,
            isActive: preset.isActive,
            frequencyConfig: preset.frequencyConfig,
            createdBy: currentUser.uid,
          })
        )
      );
      
      await fetchPresets();
      toast.success(`Successfully imported ${importedPresets.length} presets`);
    } catch (error) {
      console.error('Error importing presets:', error);
      throw error;
    }
  };

  const getCategoryIcon = (category: HabitCategory) => {
    return habitBadges[category]?.icon || '📋';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">
            {isRTL ? 'إدارة العادات المحددة مسبقاً' : 'Habit Preset Management'}
          </h2>
          <p className="text-muted-foreground">
            {isRTL 
              ? 'إدارة قائمة العادات المحددة مسبقاً المتاحة للمستخدمين والمدربين'
              : 'Manage the list of preset habits available to customers and coaches'
            }
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowExcelUpload(true)} variant="outline">
            <FileSpreadsheet className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
            {isRTL ? 'استيراد من Excel' : 'Import from Excel'}
          </Button>
          <Button onClick={() => handleOpenDialog()}>
            <Plus className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
            {isRTL ? 'إضافة عادة جديدة' : 'Add Preset'}
          </Button>
        </div>
      </div>

      {/* Presets Table */}
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{isRTL ? 'الفئة' : 'Category'}</TableHead>
              <TableHead>{isRTL ? 'الاسم (EN)' : 'Name (EN)'}</TableHead>
              <TableHead>{isRTL ? 'الاسم (AR)' : 'Name (AR)'}</TableHead>
              <TableHead>{isRTL ? 'الوصف' : 'Description'}</TableHead>
              <TableHead>{isRTL ? 'الحالة' : 'Status'}</TableHead>
              <TableHead className="text-right">{isRTL ? 'الإجراءات' : 'Actions'}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {presets.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground">
                  {isRTL ? 'لا توجد عادات محددة مسبقاً' : 'No presets found'}
                </TableCell>
              </TableRow>
            ) : (
              presets.map((preset) => (
                <TableRow key={preset.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span>{getCategoryIcon(preset.category)}</span>
                      <span>{t(`habits.categories.${preset.category}`)}</span>
                    </div>
                  </TableCell>
                  <TableCell>{preset.name}</TableCell>
                  <TableCell>{preset.nameAr || '-'}</TableCell>
                  <TableCell className="max-w-xs truncate">
                    {preset.description || '-'}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={preset.isActive}
                        onCheckedChange={() => handleToggleStatus(preset)}
                      />
                      <Badge variant={preset.isActive ? 'default' : 'secondary'}>
                        {preset.isActive 
                          ? (isRTL ? 'نشط' : 'Active')
                          : (isRTL ? 'غير نشط' : 'Inactive')
                        }
                      </Badge>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleOpenDialog(preset)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeletePreset(preset)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl" dir={dir}>
          <DialogHeader>
            <DialogTitle className={isRTL ? 'text-right' : 'text-left'}>
              {editingPreset
                ? (isRTL ? 'تعديل العادة' : 'Edit Preset')
                : (isRTL ? 'إضافة عادة جديدة' : 'Add New Preset')
              }
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{isRTL ? 'الاسم (English)' : 'Name (English)'}</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Drink 8 glasses of water"
                />
              </div>
              <div>
                <Label>{isRTL ? 'الاسم (Arabic)' : 'Name (Arabic)'}</Label>
                <Input
                  value={formData.nameAr}
                  onChange={(e) => setFormData({ ...formData, nameAr: e.target.value })}
                  placeholder="مثال: شرب 8 أكواب من الماء"
                  dir="rtl"
                />
              </div>
            </div>

            <div>
              <Label>{isRTL ? 'الفئة' : 'Category'}</Label>
              <Select
                value={formData.category}
                onValueChange={(value: HabitCategory) =>
                  setFormData({ ...formData, category: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {HABIT_CATEGORY_ORDER.map((category) => (
                    <SelectItem key={category} value={category}>
                      <span className="flex items-center gap-2">
                        <span>{getCategoryIcon(category)}</span>
                        <span>{t(`habits.categories.${category}`)}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>{isRTL ? 'الوصف (English)' : 'Description (English)'}</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Optional description for this habit..."
                rows={3}
              />
            </div>

            <div>
              <Label>{isRTL ? 'الوصف (Arabic)' : 'Description (Arabic)'}</Label>
              <Textarea
                value={formData.descriptionAr}
                onChange={(e) => setFormData({ ...formData, descriptionAr: e.target.value })}
                placeholder="وصف اختياري لهذه العادة..."
                rows={3}
                dir="rtl"
              />
            </div>

            {/* Frequency Configuration */}
            <div className="pt-4 border-t">
              <h3 className={`text-sm font-medium mb-4 ${isRTL ? 'text-right' : 'text-left'}`}>
                {isRTL ? 'إعدادات التكرار والمدة (اختياري)' : 'Frequency & Duration Settings (Optional)'}
              </h3>
              <FrequencySelector
                value={frequencyConfig}
                onChange={setFrequencyConfig}
                disabled={isSaving}
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowDialog(false)}>
                {isRTL ? 'إلغاء' : 'Cancel'}
              </Button>
              <Button onClick={handleSave} disabled={isSaving}>
                {isSaving ? (
                  <>
                    <Loader2 className={`h-4 w-4 animate-spin ${isRTL ? 'ml-2' : 'mr-2'}`} />
                    {isRTL ? 'جاري الحفظ...' : 'Saving...'}
                  </>
                ) : (
                  isRTL ? 'حفظ' : 'Save'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deletePreset} onOpenChange={() => setDeletePreset(null)}>
        <AlertDialogContent dir={dir}>
          <AlertDialogHeader>
            <AlertDialogTitle className={isRTL ? 'text-right' : 'text-left'}>
              {isRTL ? 'حذف العادة المحددة' : 'Delete Preset'}
            </AlertDialogTitle>
            <AlertDialogDescription className={isRTL ? 'text-right' : 'text-left'}>
              {isRTL
                ? `هل أنت متأكد من حذف "${deletePreset?.name}"؟ لا يمكن التراجع عن هذا الإجراء.`
                : `Are you sure you want to delete "${deletePreset?.name}"? This action cannot be undone.`
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className={isRTL ? 'flex-row-reverse' : ''}>
            <AlertDialogCancel>{isRTL ? 'إلغاء' : 'Cancel'}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">
              {isRTL ? 'حذف' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Excel Upload Dialog */}
      <HabitPresetExcelUpload
        open={showExcelUpload}
        onOpenChange={setShowExcelUpload}
        onImport={handleExcelImport}
      />
    </div>
  );
}

